import { format } from "timeago.js";

function ensureUtc(time) {
  if (typeof time === 'string' && !time.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(time)) {
    return time + 'Z';
  }
  return time;
}

export default class Utils {
  /**
   * Resolves how an entity (org, blob, user) is identified in the UI: its name if it has one, its
   * alias otherwise. `fallback` covers the window before the entity has loaded, where callers pass
   * the alias from the route params.
   */
  static displayName(entity, fallback) {
    if (entity) {
      if (entity.name) return entity.name;
      if (entity.alias) return entity.alias;
    }
    return fallback;
  }

  static formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  static formatTimeAgo(time) {
    return format(time + " UTC");
  }

  static formatDateTime(time, timezone) {
    const options = {
      weekday: "long", year: "numeric", month: "long", day: "numeric",
      hour: "numeric", minute: "numeric", second: "numeric"
    };
    if (timezone) options.timeZone = timezone;
    return new Date(timezone ? ensureUtc(time) : time).toLocaleDateString('en-us', options);
  }

  // Format a naive datetime string (no timezone suffix) like "2025-03-12T15:30:30"
  // by treating its components as-is, without applying browser local interpretation.
  // Used for fields like invocation_time which carry their own timezone separately.
  static formatNaiveDateTime(time) {
    if (!time) return "";
    const [datePart, timePart] = time.split("T");
    if (!datePart || !timePart) return time;
    const [y, mo, d] = datePart.split("-").map(Number);
    const [h, mi, sec] = timePart.split(":").map(Number);
    const date = new Date(Date.UTC(y, mo - 1, d, h, mi, sec || 0));
    return date.toLocaleString('en-US', {
      timeZone: 'UTC',
      year: "numeric", month: "long", day: "numeric",
      hour: "numeric", minute: "numeric", second: "numeric"
    });
  }

  static formatCompactDateTime(time, timezone) {
    const options = {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit"
    };
    if (timezone) options.timeZone = timezone;
    return new Date(timezone ? ensureUtc(time) : time).toLocaleString('en-US', options);
  }

  static formatTime(time, timezone) {
    const options = {
      hour: "numeric", minute: "numeric", second: "numeric"
    };
    if (timezone) options.timeZone = timezone;
    return new Date(timezone ? ensureUtc(time) : time).toLocaleTimeString('en-us', options);
  }

  static currentTimeUtcIso() {
    return new Date().toISOString().slice(0, -1);
  }

  static deepCopy(obj) {
    return JSON.parse(JSON.stringify(obj));
  }

  static generateUniqueId() {
    return crypto.randomUUID();
  }
}
